
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/components/AuthProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Loader2 } from "lucide-react";

// Direct imports instead of lazy loading to debug the module import issue
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import ProjectDeveloperDashboard from "./pages/ProjectDeveloperDashboard";
import Marketplace from "./pages/Marketplace";
import Analytics from "./pages/Analytics";
import Integration from "./pages/Integration";
import NewProject from "./pages/NewProject";
import ProjectDetail from "./pages/ProjectDetail";
import Search from "./pages/Search";
import Portfolio from "./pages/Portfolio";
import ESGReporting from "./pages/ESGReporting";
import TradingHistory from "./pages/TradingHistory";
import Compliance from "./pages/Compliance";
import Auth from "./pages/Auth";
import Settings from "./pages/Settings";
import Support from "./pages/Support";
import NotFound from "./pages/NotFound";
import ProjectSearchProjects from "./pages/ProjectSearchProjects";
import ProjectMarketplace from "./pages/ProjectMarketplace";
import ProjectAnalytics from "./pages/ProjectAnalytics";
import ProjectMyProjects from "./pages/ProjectMyProjects";
import ProjectSalesRevenue from "./pages/ProjectSalesRevenue";
import ProjectVerification from "./pages/ProjectVerification";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const LoadingFallback = () => {
  console.log('LoadingFallback rendered');
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">Loading...</p>
        <p className="text-xs text-muted-foreground mt-2">If this persists, check console for errors</p>
      </div>
    </div>
  );
};

const AuthenticatedApp = () => {
  const { user, loading } = useAuth();

  console.log('AuthenticatedApp rendered:', { 
    user: !!user, 
    loading
  });

  if (loading) {
    console.log('Still loading auth...');
    return <LoadingFallback />;
  }

  return (
    <Routes>
        {/* Public routes */}
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Landing />} />
        <Route path="/auth" element={user ? <Navigate to="/dashboard" replace /> : <Auth />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/project-developer" element={
          <ProtectedRoute>
            <ProjectDeveloperDashboard />
          </ProtectedRoute>
        } />
        <Route path="/marketplace" element={
          <ProtectedRoute>
            <Marketplace />
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/integration" element={
          <ProtectedRoute>
            <Integration />
          </ProtectedRoute>
        } />
        <Route path="/projects/new" element={
          <ProtectedRoute>
            <NewProject />
          </ProtectedRoute>
        } />
        <Route path="/project/:id" element={
          <ProtectedRoute>
            <ProjectDetail />
          </ProtectedRoute>
        } />
        <Route path="/search" element={
          <ProtectedRoute>
            <Search />
          </ProtectedRoute>
        } />
        <Route path="/project-search" element={
          <ProtectedRoute>
            <ProjectSearchProjects />
          </ProtectedRoute>
        } />
        <Route path="/project-marketplace" element={
          <ProtectedRoute>
            <ProjectMarketplace />
          </ProtectedRoute>
        } />
        <Route path="/project-analytics" element={
          <ProtectedRoute>
            <ProjectAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/project-projects" element={
          <ProtectedRoute>
            <ProjectMyProjects />
          </ProtectedRoute>
        } />
        <Route path="/project-sales" element={
          <ProtectedRoute>
            <ProjectSalesRevenue />
          </ProtectedRoute>
        } />
        <Route path="/project-verification" element={
          <ProtectedRoute>
            <ProjectVerification />
          </ProtectedRoute>
        } />
        <Route path="/portfolio" element={
          <ProtectedRoute>
            <Portfolio />
          </ProtectedRoute>
        } />
        <Route path="/esg-reporting" element={
          <ProtectedRoute>
            <ESGReporting />
          </ProtectedRoute>
        } />
        <Route path="/trading-history" element={
          <ProtectedRoute>
            <TradingHistory />
          </ProtectedRoute>
        } />
        <Route path="/compliance" element={
          <ProtectedRoute>
            <Compliance />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/support" element={
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        } />
        
        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <div className="min-h-screen bg-background">
            <AuthenticatedApp />
          </div>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
